import React, { useMemo, useState } from "react";
import type { PeopleDetailsActivitiesProps } from "../prop-types";
import { AuthAction, usePermissionContext } from "@/react/providers/permission-context-provider";
import type {
   ActivitiesResponseProps,
   ActivitiesSpecificOptionType,
   ExtendedActivitiesResponseProps,
} from "../../types";
import { dateSortOptions, personSpecificActivityOptions } from "@/react/shared/constants";
import {
   ActivityCategory,
   IntegratedEntity,
} from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import { useFindActivityPaginatedQuery } from "@/react/components/common/queries/queries";
import { Card, useI18nContext } from "@procore/core-react";
import ActivityList from "../../activities/activity-list";
import { Order } from "@laborchart-modules/common/dist/reql-builder/query-definitions";
import {
   getActionText,
   getTargetText,
   renderActivityText,
   renderAssignmentsActivityText,
   renderAssignmentsSegmentsActivityText,
   renderNotesActivityText,
   renderSimpleActivityText,
   renderTimeOffActivityTest,
} from "../../activities/activity-helper";
import { PersonActivitiesInfoCard } from "./person-activities-info-card";

const ACTIVITIES_LIMIT = 25;

export const PersonDetailsActivities: React.FC<PeopleDetailsActivitiesProps> = ({ personId }) => {
   const I18n = useI18nContext();
   const { checkAuthAction } = usePermissionContext();

   // Sorting state (client-side)
   const [sortOption, setSortOption] = useState(dateSortOptions[1]);

   // Category filter state
   const [sortPersonSpecificOption, setSortPersonSpecificOption] =
      useState<ActivitiesSpecificOptionType>(personSpecificActivityOptions[1]);

   // Build dropdown options based on permissions
   const personSpecificOptions = useMemo(() => {
      const permissionMap: Record<string, AuthAction> = {
         "2": AuthAction.VIEW_PEOPLE_ATTACHMENTS,
         "4": AuthAction.VIEW_PEOPLE_NOTES,
         "5": AuthAction.VIEW_PEOPLE_TAGS,
         "6": AuthAction.VIEW_PEOPLE_TIMEOFF,
      };

      return personSpecificActivityOptions.map((option) => {
         const requiredPermission = permissionMap[option.id];
         if (requiredPermission && !checkAuthAction(requiredPermission)) {
            return { ...option, show: false };
         }
         return option;
      });
   }, [checkAuthAction]);

   // Determine which categories to fetch based on filter selection
   const categoriesToFetch = useMemo(() => {
      if (sortPersonSpecificOption.id === "0") {
         // "All" selected
         return [];
      }

      // Specific category selected
      const categories = [sortPersonSpecificOption.value];

      // Special case: Assignments also needs segments
      if (sortPersonSpecificOption.value === ActivityCategory.PERSON_ASSIGNMENTS) {
         categories.push(ActivityCategory.PERSON_ASSIGNMENTS + "_segments");
      }

      return categories;
   }, [sortPersonSpecificOption]);

   // Single query with useInfiniteQuery
   const {
      data: activitiesData,
      isLoading: activitiesLoading,
      fetchNextPage,
      isFetchingNextPage,
   } = useFindActivityPaginatedQuery(
      personId,
      IntegratedEntity.PEOPLE,
      categoriesToFetch,
      ACTIVITIES_LIMIT,
   );

   // Derive values from query
   const allActivities = useMemo(() => {
      return activitiesData?.pages.flatMap((page) => page?.data ?? []) ?? [];
   }, [activitiesData?.pages]);

   const totalPossible = activitiesData?.pages?.[0]?.pagination?.total_possible ?? 0;

   // Sort activities client-side
   const sortedActivities = useMemo(() => {
      if (!allActivities.length) return [];
      return [...allActivities].sort((a, b) => {
         const dateA = new Date(a.created_at).getTime();
         const dateB = new Date(b.created_at).getTime();
         return sortOption.value === Order.ASCENDING ? dateA - dateB : dateB - dateA;
      });
   }, [allActivities, sortOption]);

   // Render activity content based on category
   const renderActivityContent = (activity: ActivitiesResponseProps) => {
      const actionTypeText = getActionText(activity?.action_type, activity?.category, I18n);
      const targetText = getTargetText(activity?.action_type, activity?.category, I18n);

      switch (activity.category) {
         case ActivityCategory.PERSON:
         case ActivityCategory.PERSON_USER_LOCK:
            return renderSimpleActivityText(activity, actionTypeText);
         case ActivityCategory.PERSON_NOTES:
            return renderNotesActivityText(activity, actionTypeText);
         case ActivityCategory.PERSON_ASSIGNMENTS_SEGMENTS:
            return renderAssignmentsSegmentsActivityText(
               activity as ExtendedActivitiesResponseProps,
               I18n,
            );
         case ActivityCategory.PERSON_ASSIGNMENTS:
            return renderAssignmentsActivityText(
               activity as ExtendedActivitiesResponseProps,
               actionTypeText,
               I18n,
            );
         case ActivityCategory.PERSON_TAGS_ATTACHMENTS:
         case ActivityCategory.PERSON_ATTACHMENTS:
         case ActivityCategory.PERSON_TAGS:
            return renderActivityText(activity, actionTypeText, targetText, I18n);
         case ActivityCategory.PERSON_TIME_OFF:
            return renderTimeOffActivityTest(activity, actionTypeText, I18n);
         case ActivityCategory.PERSON_INFO:
            return <PersonActivitiesInfoCard activity={activity} />;
      }
   };

   return (
      <Card style={{ marginBottom: "5px" }}>
         <ActivityList
            title="views.company.workforce_planning.people_details_activities.title"
            subtext="views.company.workforce_planning.people_details_activities.activities_sub_text"
            sortActivitiesOptions={personSpecificOptions}
            sortActivitiesSpecificOption={sortPersonSpecificOption}
            onActivitiesSortChange={setSortPersonSpecificOption}
            sortOptions={dateSortOptions}
            sortOption={sortOption}
            onSortChange={setSortOption}
            activities={sortedActivities}
            renderActivityContent={renderActivityContent}
            loadMoreHandler={fetchNextPage}
            limit={totalPossible}
            loadMoreLabel="views.company.workforce_planning.people_details_activities.load_more"
            emptyTitle={I18n.t(
               "views.company.workforce_planning.people_details_activities.activities_empty_state_title",
            )}
            emptyDescription={I18n.t(
               "views.company.workforce_planning.people_details_activities.activities_empty_state_description",
            )}
            isLoading={activitiesLoading}
            isFetchingNextPage={isFetchingNextPage}
         />
      </Card>
   );
};
